import React, { memo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { MdLogout, MdChevronLeft, MdMenu } from 'react-icons/md';
import { useAuth } from '../auth/AuthContext';
import { getNavItems, ROLES, normalizeRole } from '../permissions';
import { navIcon } from '../config/navIcons';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import { useLiveData } from '../hooks/useLiveData';
import { ipApi } from '../services/api';
import { mergeEmrSettings } from '../config/emrSettings';

export default memo(function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: emrData } = useLiveData(() => ipApi.getEmrSettings(), []);
  const roleModuleAccess = mergeEmrSettings(emrData?.settings).roleModuleAccess || {};
  const items = getNavItems(normalizeRole(user?.role), roleModuleAccess);
  const roleColor = ROLES[normalizeRole(user?.role)]?.color || '#0d6e6a';

  const sections = [...new Set(items.map((i) => i.section))];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = (user?.name || 'H').charAt(0).toUpperCase();

  return (
    <aside className={`sidebar${collapsed ? ' is-collapsed' : ''}`} style={{ width: collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)' }}>
      <div className="sidebar-brand">
        <span className="sidebar-logo-wrap">
          <img src="/hcen_logo.png" alt="HCEN" className="sidebar-logo" />
          <span className="sidebar-logo-dot" />
        </span>
        {!collapsed && (
          <div>
            <div className="sidebar-title">HCEN HMS</div>
            <div className="sidebar-sub">Hospital System</div>
          </div>
        )}
      </div>

      <button type="button" className="sidebar-toggle" onClick={onToggle} aria-label="Toggle sidebar">
        {collapsed ? <MdMenu /> : <MdChevronLeft />}
      </button>

      <WorkspaceSwitcher emrSettings={emrData?.settings} collapsed={collapsed} />

      <nav className="sidebar-nav">
        {sections.map((section) => {
          const sectionItems = items.filter((i) => i.section === section);
          return (
            <div key={section} className="sidebar-section">
              {!collapsed ? (
                <div className="sidebar-section-label">{section}</div>
              ) : (
                <div className="sidebar-section-divider" />
              )}
              {sectionItems.map(({ path, label }) => {
                const Icon = navIcon(path);
                return (
                  <NavLink key={path} to={path} className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`} title={label}>
                    <span className="nav-ico"><Icon /></span>
                    {!collapsed && <span className="nav-label">{label}</span>}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-foot">
        {!collapsed && (
          <div className="sidebar-user">
            <span className="sidebar-user-avatar" style={{ background: `linear-gradient(135deg, ${roleColor}, ${roleColor}cc)` }}>
              {initials}
            </span>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name || 'Staff'}</div>
              <div className="sidebar-user-sub">
                <span className="sidebar-user-dot" style={{ background: roleColor }} />
                {user?.roleLabel || 'Staff'}
              </div>
            </div>
          </div>
        )}
        <button type="button" className="sidebar-logout" onClick={handleLogout} title="Logout">
          <MdLogout /> {!collapsed && 'Logout'}
        </button>
      </div>
    </aside>
  );
});
